import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
} from "@components/ui/table";
import { Trans, useLingui } from "@lingui/react/macro";
import SearchRow from "./SearchRow";
import { SearchSubscription } from "@/types/SearchSubscription";
import { Organization } from "@/types/Organization";
import { Search } from "@/types/Search";
import { User } from "@/types/User";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SearchSubscriptionInterval } from "@/constants";
import {
  createSearchSubscription,
  deleteSearchSubscription,
  getAvailableSearchSubscriptions,
} from "@/api/endpoints/alert-subscriptions";
import { toast } from "sonner";
import { getOngoingSearches } from "@/api/endpoints/searches";
import { getOrganizationUserSearchSubscriptions } from "@/api/endpoints/organization";
import { Skeleton } from "@components/ui/skeleton";
import ErrorComponent from "@components/ui/error";

type SearchSubscriptionsTableProps = {
  isShowingSubscribers?: boolean;
  orgUuid: Organization["uuid"];
  userId: User["id"];
  allowedIntervals?: SearchSubscriptionInterval[];
};

export default function SearchSubscriptionsTable({
  isShowingSubscribers = true,
  orgUuid,
  userId,
  allowedIntervals,
  ...rest
}: SearchSubscriptionsTableProps & React.ComponentProps<"table">) {
  const { t } = useLingui();

  // Server state
  const queryClient = useQueryClient();

  const {
    data: searchesData,
    error: searchesError,
    isLoading: isSearchesLoading,
  } = useQuery({
    queryKey: [orgUuid, "ongoing-searches"],
    queryFn: () => getOngoingSearches(),
  });

  const {
    data: allIntervalsData,
    error: allIntervalsError,
    isLoading: isAllIntervalsLoading,
  } = useQuery({
    queryKey: [orgUuid, userId, "available-search-subscriptions"],
    queryFn: () =>
      getAvailableSearchSubscriptions({
        organization_uuid: orgUuid,
        user_id: userId,
      }),
  });

  const {
    data: searchSubscriptionsData,
    error: searchSubscriptionsError,
    isLoading: isSearchSubscriptionsLoading,
  } = useQuery({
    queryKey: [orgUuid, userId, "search-subscriptions"],
    queryFn: () => {
      return getOrganizationUserSearchSubscriptions({
        organization_uuid: orgUuid,
        user_id: userId,
      });
    },
  });

  function refetchData() {
    queryClient.invalidateQueries({
      queryKey: [orgUuid, userId, "search-subscriptions"],
    });
    queryClient.invalidateQueries({
      queryKey: [orgUuid, "ongoing-searches"],
    });
  }

  const {
    mutate: mutateCreateSearchSubscription,
    isPending: isCreatingSearchSubscription,
  } = useMutation({
    mutationFn: async ({
      interval,
      searchUuid,
    }: {
      interval: SearchSubscriptionInterval;
      searchUuid: Search["uuid"];
    }) => {
      return createSearchSubscription({
        search_uuid: searchUuid,
        organization_uuid: orgUuid,
        user_id: userId,
        interval,
      });
    },
    onSuccess: (response) => {
      if (!response.ok) {
        throw new Error();
      }

      refetchData();
    },
    onError: () => {
      toast.error(t`Kunde inte lägga till prenumerationen`);
    },
  });

  const {
    mutate: mutateDeleteSearchSubscription,
    isPending: isDeletingSearchSubscription,
  } = useMutation({
    mutationFn: (subId: SearchSubscription["id"]) => {
      return deleteSearchSubscription({
        organization_uuid: orgUuid,
        user_id: userId,
        sub_id: subId,
      });
    },
    onSuccess: (response) => {
      if (!response.ok) {
        throw new Error();
      }

      refetchData();
    },
    onError: () => {
      toast.error(t`Kunde inte ta bort prenumerationen`);
    },
  });

  // Handlers
  const handleCreateSearchSubscription = (values: {
    interval: SearchSubscriptionInterval;
    searchUuid: Search["uuid"];
  }) => {
    mutateCreateSearchSubscription(values);
  };

  const handleDeleteSearchSubscription = (id: SearchSubscription["id"]) => {
    mutateDeleteSearchSubscription(id);
  };

  if (searchesData?.ok && allIntervalsData?.ok && searchSubscriptionsData?.ok) {
    return (
      <Table data-testid="active-alerts-list" {...rest}>
        <TableHeader>
          <TableRow>
            <TableHead>
              <Trans>Bevakningens titel</Trans>
            </TableHead>

            <TableHead>
              <Trans>Prenumeration</Trans>
            </TableHead>

            {isShowingSubscribers && (
              <TableHead>
                <Trans>Prenumeranter</Trans>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {searchesData.data.map((search) => (
            <SearchRow
              userId={userId}
              orgUuid={orgUuid}
              search={search}
              key={search.uuid}
              isShowingSubscribers={isShowingSubscribers}
              isDeletingSearchSubscription={isDeletingSearchSubscription}
              isCreatingSearchSubscription={isCreatingSearchSubscription}
              availableSearchSubscriptions={allIntervalsData.data}
              allowedIntervals={allowedIntervals}
              searchSubscriptions={searchSubscriptionsData.data.subscriptions.filter(
                (as) => as.search_uuid === search.uuid
              )}
              onDeleteSearchSubscription={handleDeleteSearchSubscription}
              onCreateSearchSubscription={(interval) => {
                handleCreateSearchSubscription({
                  searchUuid: search.uuid,
                  interval,
                });
              }}
            />
          ))}
        </TableBody>
      </Table>
    );
  }

  if (
    isSearchesLoading ||
    isAllIntervalsLoading ||
    isSearchSubscriptionsLoading
  ) {
    return <Skeleton className="h-56 w-full" />;
  }

  if (
    !searchesData?.ok ||
    !allIntervalsData?.ok ||
    !searchSubscriptionsData?.ok
  ) {
    return (
      <ErrorComponent
        heading={t`Åh nej!`}
        description={t`Något gick snett med att ladda bevakningarna. Vänligen försök igen senare`}
        error={searchesError || allIntervalsError || searchSubscriptionsError}
      />
    );
  }

  return null;
}
